import { supabase } from './supabase';
import { callRpcViaFetch } from './rpc-bypass';
import * as Sentry from '@sentry/react';
import { Apartment } from '../types';

export interface FollowedBroker {
  broker_id: string;
  followed_at: string;
  has_new_listings: boolean;
  new_listing_count: number;
}

/**
 * Follow a broker (auto-called when opening a shared listing link)
 * Returns false if trying to self-follow
 */
export async function followBroker(brokerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('follow_broker', { target_broker_id: brokerId });

    if (error) throw error;
    
    return data === true;
  } catch (error) {
    console.error('Error following broker:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'follow' } 
    });
    return false;
  }
}

/**
 * Unfollow a broker
 */
export async function unfollowBroker(brokerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('unfollow_broker', { target_broker_id: brokerId });

    if (error) throw error;
    
    return data === true;
  } catch (error) {
    console.error('Error unfollowing broker:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'unfollow' } 
    });
    return false;
  }
}

/**
 * Get all followed brokers with their story status
 * Returns brokers sorted by active status (new listings first)
 */
export async function getFollowedBrokersWithStatus(): Promise<FollowedBroker[]> {
  try {
    const { data, error } = await supabase
      .rpc('get_followed_brokers_with_story_status');

    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching followed brokers:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'get-followed' } 
    });
    return [];
  }
}

/**
 * Mark a broker's story as viewed (updates last_viewed_at)
 */
export async function markStoryViewed(brokerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .rpc('mark_broker_story_viewed', { target_broker_id: brokerId });

    if (error) throw error;
    
    return data === true;
  } catch (error) {
    console.error('Error marking story viewed:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'mark-viewed' } 
    });
    return false;
  }
}

/**
 * Get new listings from a broker (for Story Mode swipe deck)
 * Returns listings created after the user's last view
 */
export async function getBrokerNewListings(brokerId: string): Promise<Apartment[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // First get the last viewed timestamp
    const { data: viewData } = await supabase
      .from('broker_story_views')
      .select('last_viewed_at')
      .eq('renter_id', user.id)
      .eq('broker_id', brokerId)
      .single();

    const lastViewedAt = viewData?.last_viewed_at || '1970-01-01T00:00:00Z';

    // Fetch new listings from this broker
    const { data, error } = await supabase
      .from('apartments')
      .select('*')
      .eq('owner_id', brokerId)
      .gt('created_at', lastViewedAt)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    return data || [];
  } catch (error) {
    console.error('Error fetching broker new listings:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'get-new-listings' } 
    });
    return [];
  }
}

/**
 * Get all listings from a broker (for Broker Profile page and Stories)
 * Uses raw fetch bypass for fast, deadlock-free data loading
 */
export async function getBrokerListings(brokerId: string): Promise<Apartment[]> {
  try {
    const { data, error } = await callRpcViaFetch<Apartment[]>('get_broker_listings_public', { 
      target_broker_id: brokerId 
    });

    if (error) {
      console.error('[getBrokerListings] RPC error:', error);
      throw error;
    }
    
    return data || [];
  } catch (error) {
    console.error('[getBrokerListings] ERROR:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'get-listings' },
      extra: { brokerId }
    });
    return [];
  }
}

/**
 * Get follower count for a broker (for dashboard)
 * If no brokerId provided, returns count for current user
 */
export async function getBrokerFollowerCount(brokerId?: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .rpc('get_broker_follower_count', { 
        target_broker_id: brokerId || null 
      });

    if (error) throw error;
    
    return data || 0;
  } catch (error) {
    console.error('Error fetching follower count:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'get-follower-count' } 
    });
    return 0;
  }
}

/**
 * Check if current user is following a broker
 * Uses RPC for consistency and better error handling
 */
export async function isFollowingBroker(brokerId: string): Promise<boolean> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    // Use the get_followed_brokers_with_story_status RPC and check if this broker is in the list
    const { data, error } = await supabase
      .rpc('get_followed_brokers_with_story_status');

    if (error) {
      // Silently fail - not critical for UX
      return false;
    }
    
    return (data || []).some((b: { broker_id: string }) => b.broker_id === brokerId);
  } catch {
    // Silently fail - not critical for UX
    return false;
  }
}

/**
 * Audience Stats interface for Broker Insights Dashboard
 */
export interface AudienceStats {
  totalFollowers: number;
  newThisWeek: number;
  avgBudget: number;
  topCities: { city: string; count: number; percentage: number }[];
  roomDistribution: { rooms: string; count: number; percentage: number }[];
  topAmenities: { amenity: string; count: number; percentage: number }[];
  insufficientData: boolean;
  // Budget brackets for bar chart
  budgetBrackets: { range: string; count: number; percentage: number }[];
  // Most common room count (for summary sentence)
  mostCommonRooms: number;
  // Top city name (for summary sentence)
  topCity: string | null;
}

/**
 * Get aggregated audience statistics for a broker's followers
 * Used in the Follower Insights Dashboard
 */
export async function getBrokerAudienceStats(brokerId?: string): Promise<AudienceStats | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_broker_audience_stats', { 
        target_broker_id: brokerId || null 
      });

    if (error) throw error;
    
    // Handle the response - it's a JSON object
    if (!data) {
      return {
        totalFollowers: 0,
        newThisWeek: 0,
        avgBudget: 0,
        topCities: [],
        roomDistribution: [],
        topAmenities: [],
        insufficientData: true,
        budgetBrackets: [],
        mostCommonRooms: 0,
        topCity: null,
      };
    }

    return {
      totalFollowers: data.totalFollowers || 0,
      newThisWeek: data.newThisWeek || 0,
      avgBudget: data.avgBudget || 0,
      topCities: data.topCities || [],
      roomDistribution: data.roomDistribution || [],
      topAmenities: data.topAmenities || [],
      insufficientData: data.insufficientData || false,
      budgetBrackets: data.budgetBrackets || [],
      mostCommonRooms: data.mostCommonRooms || 0,
      topCity: data.topCity || null,
    };
  } catch (error) {
    console.error('Error fetching audience stats:', error);
    Sentry.captureException(error, { 
      tags: { feature: 'broker-stories', action: 'get-audience-stats' } 
    });
    return null;
  }
}

/**
 * Calculate how well a listing matches the broker's audience preferences
 * Returns a score from 0-100
 */
export function calculateListingMatchScore(
  apartment: Apartment, 
  stats: AudienceStats
): number {
  if (stats.insufficientData || stats.totalFollowers < 5) {
    return 0;
  }

  let score = 0;

  // Price within audience budget range: +40 points
  // Check if price is within the most common budget brackets
  const avgBudget = stats.avgBudget;
  if (avgBudget > 0) {
    const priceDiff = Math.abs(apartment.price - avgBudget);
    const priceRange = avgBudget * 0.3; // 30% tolerance
    if (priceDiff <= priceRange) {
      score += 40;
    } else if (priceDiff <= priceRange * 2) {
      score += 20; // Partial points for close match
    }
  }

  // Room count matches top preference: +30 points
  if (stats.mostCommonRooms > 0) {
    if (apartment.rooms === stats.mostCommonRooms) {
      score += 30;
    } else if (Math.abs(apartment.rooms - stats.mostCommonRooms) === 1) {
      score += 15; // Partial points for ±1 room
    }
  }

  // Location matches top city: +20 points
  if (stats.topCity && apartment.city) {
    const apartmentCity = apartment.city.toLowerCase().trim();
    const topCity = stats.topCity.toLowerCase().trim();
    if (apartmentCity === topCity || apartmentCity.includes(topCity) || topCity.includes(apartmentCity)) {
      score += 20;
    }
  }

  // Has top amenities: +10 points (distributed across matching amenities)
  if (stats.topAmenities.length > 0) {
    const apartmentAmenities = [
      ...apartment.amenities,
      apartment.petFriendly ? 'petFriendly' : '',
      apartment.private_parking ? 'parking' : '',
      apartment.elevator ? 'elevator' : '',
      apartment.storage ? 'storage' : '',
    ].filter(Boolean).map(a => a.toLowerCase());

    const matchingAmenities = stats.topAmenities.filter(ta => 
      apartmentAmenities.some(aa => 
        aa.includes(ta.amenity.toLowerCase()) || ta.amenity.toLowerCase().includes(aa)
      )
    );

    // Up to 10 points based on matching amenities (2 points each, max 5)
    score += Math.min(matchingAmenities.length * 2, 10);
  }

  return Math.min(score, 100);
}

/**
 * Check if a user/guest has an active subscription for a broker
 * For logged-in users: Checks both user.user_metadata.phone AND active subscription in listing_subscriptions
 * For guests: Checks listing_subscriptions by subscriber_phone only
 */
export async function hasActiveSubscription(
  brokerId: string,
  phone?: string,
  userId?: string
): Promise<boolean> {
  try {
    // For logged-in users, check subscription by user_id
    if (userId) {
      const { data, error } = await supabase
        .from('listing_subscriptions')
        .select('id')
        .eq('broker_id', brokerId)
        .eq('is_active', true)
        .eq('subscriber_user_id', userId)
        .limit(1);

      if (error) {
        // If error is just "no rows found", that's fine - return false
        if (error.code !== 'PGRST116') {
          console.error('Error checking subscription by userId:', error);
        }
        return false;
      }
      return !!data && data.length > 0;
    }

    // For guests (not logged in), we can't check subscriptions
    // They need to log in to see their subscription status
    return false;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    Sentry.captureException(error, {
      tags: { feature: 'broker-stories', action: 'check-subscription' }
    });
    return false;
  }
}

/**
 * Unsubscribe from a broker's WhatsApp notifications
 * Sets is_active to false instead of deleting the record
 * For logged-in users: Updates by user_id
 */
export async function unsubscribeFromBroker(
  brokerId: string,
  phone?: string,
  userId?: string
): Promise<boolean> {
  try {
    // Only logged-in users can unsubscribe through the UI
    if (!userId) {
      console.error('User must be logged in to unsubscribe');
      return false;
    }

    const { error } = await supabase
      .from('listing_subscriptions')
      .update({ is_active: false })
      .eq('broker_id', brokerId)
      .eq('subscriber_user_id', userId);

    if (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
    
    return true;
  } catch (error) {
    console.error('Error unsubscribing from broker:', error);
    Sentry.captureException(error, {
      tags: { feature: 'broker-stories', action: 'unsubscribe' }
    });
    return false;
  }
}